// Knowledge › Documents (A2 step A, chatbot_v2_mono docs/superpowers/specs/2026-10-06-ai-console-v2-first-design.md).
// No imports on purpose: `node --test` loads this file directly (ui/tests/documentForm.test.ts).
//
// Two kinds of document reach the same hub RAG store (Qdrant rag_faqs_<profile>, searched by tool-service /chat):
//  - manual: text or a file a person added here (or on the v1/v2 documents pages) — edited and deleted by hand;
//  - auto: one per Knowledge-builder chunk (llm-tester writes metadata.origin = doc_chunker, source = the page URL)
//    and replaced on every re-crawl / re-index — shown, never edited by hand.
// `source` is only a label: the hub never downloads it.

export const DOCUMENT_TYPES = ['manual', 'tutorial', 'rule', 'product', 'faq', 'other'] as const;
export const DOCS_PAGE_SIZE = 50;
/** The hub's upload limit (backend rag/document-upload.middleware.ts MAX_UPLOAD_BYTES). */
export const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;
const AUTO_ORIGIN = 'doc_chunker';
const FILE_EXT = /\.(pdf|txt|md|markdown)$/i;

export type ContentSource = 'text' | 'file';
export type OriginFilter = '' | 'manual' | 'auto';

export interface DocumentForm {
  title: string;
  documentType: string;
  category: string;
  language: string;
  /** Comma-separated in the form; sent as a list. */
  tags: string;
  contentSource: ContentSource;
  content: string;
}

export function emptyDocumentForm(): DocumentForm {
  return { title: '', documentType: 'other', category: '', language: 'mn', tags: '', contentSource: 'text', content: '' };
}

export function isAutoDocument(doc: { metadata?: Record<string, unknown> | null }): boolean {
  return doc.metadata?.origin === AUTO_ORIGIN;
}

/** Query string for hub `GET /api/rag/documents`: always paged (envelope) and scoped to the bot. */
export function documentsListQuery(p: {
  system: string;
  botId: string;
  page: number;
  origin?: OriginFilter;
  documentType?: string;
  category?: string;
}): string {
  const q = new URLSearchParams({ system: p.system, botId: p.botId });
  if (p.origin) q.set('origin', p.origin);
  if (p.documentType) q.set('documentType', p.documentType);
  if (p.category) q.set('category', p.category);
  q.set('limit', String(DOCS_PAGE_SIZE));
  q.set('offset', String(Math.max(0, p.page - 1) * DOCS_PAGE_SIZE));
  return q.toString();
}

export function pageCount(total: number): number {
  return Math.max(1, Math.ceil(total / DOCS_PAGE_SIZE));
}

/** The first problem with the form, or null when it can be saved. */
export function validateDocumentForm(
  f: DocumentForm,
  ctx: { editing: boolean; file: { name: string; size: number } | null },
): string | null {
  if (!f.title.trim()) return 'Enter a title.';
  if (!ctx.editing && !f.documentType) return 'Choose a document type.';
  if (f.contentSource === 'text') return f.content.trim() ? null : 'Enter the text.';
  if (!ctx.file) return 'Choose a file.';
  if (!FILE_EXT.test(ctx.file.name)) return 'Only PDF, TXT or MD files.';
  if (ctx.file.size > MAX_UPLOAD_BYTES) return `The file is larger than ${MAX_UPLOAD_BYTES / 1024 / 1024} MB.`;
  return null;
}

/**
 * The fields to send (JSON body, or the non-file parts of the multipart upload). A create names the bot and the type;
 * an edit sends neither (the document already belongs to a bot; the type is fixed after create). A file upload sends
 * no `content` — the hub extracts it.
 */
export function documentFields(
  f: DocumentForm,
  ctx: { editing: boolean; system: string; botId: string },
): Record<string, string | string[]> {
  const out: Record<string, string | string[]> = {};
  if (!ctx.editing) {
    out.system = ctx.system;
    out.botId = ctx.botId;
    // Create only: the hub's UpdateDocumentDto has no documentType and its ValidationPipe rejects unknown fields.
    out.documentType = f.documentType;
  }
  out.title = f.title.trim();
  out.language = f.language || 'mn';
  if (f.category.trim()) out.category = f.category.trim();
  const tags = f.tags.split(',').map((t) => t.trim()).filter(Boolean);
  if (tags.length) out.tags = tags;
  if (f.contentSource === 'text') out.content = f.content;
  return out;
}

export function formFromDocument(d: {
  title?: string | null;
  documentType?: string | null;
  category?: string | null;
  language?: string | null;
  tags?: string[] | null;
  content?: string | null;
}): DocumentForm {
  return {
    title: d.title ?? '',
    documentType: d.documentType ?? 'other',
    category: d.category ?? '',
    language: d.language ?? 'mn',
    tags: (d.tags ?? []).join(', '),
    contentSource: 'text',
    content: d.content ?? '',
  };
}

export function sourceLabel(d: { source?: string | null }): string {
  return d.source?.trim() || '—';
}

export function deleteWarning(d: { metadata?: Record<string, unknown> | null }): string {
  return isAutoDocument(d)
    ? 'Delete this document? It was made by the Knowledge builder — the next re-crawl / re-index of its page adds it back.'
    : 'Delete this document? Its chunks are removed from the bot’s knowledge.';
}
